import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import { Icon } from "../mui";

const c = brainTokens.color;

export interface ConflictHolder {
  displayName: string;
  sinceLabel: string;
  unpushed: boolean;
  heldDays?: number;
}

/**
 * Advisory conflict warning — never hard-blocks. Amber when holder has unpushed
 * work; calm grey when their changes are already in the brain.
 */
export function ConflictWarningBanner({
  holders,
  onContinueAnyway,
}: {
  holders: ConflictHolder[];
  onContinueAnyway?: () => void;
}) {
  if (!holders.length) return null;
  const primary = holders[0]!;
  const amber = primary.unpushed;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 1.75,
        mb: 2,
        borderRadius: `${brainTokens.radius.card}px`,
        border: `1px solid ${amber ? c.warnBorder : c.border}`,
        background: amber ? c.warnBg : c.panelBg2,
      }}
    >
      <Icon name="warning" size={18} style={{ color: amber ? c.warnText : c.muted2, marginTop: 2 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
          Checked out by {primary.displayName}
        </Box>
        <Box sx={{ fontSize: 13, color: c.ink3, mt: 0.35 }}>
          since {primary.sinceLabel}
          {primary.unpushed
            ? ` — with unpushed work${primary.heldDays != null ? ` (nothing pushed for ${primary.heldDays}d)` : ""}`
            : " — their changes are already in the brain"}
          . Connect with {primary.displayName} before continuing.
        </Box>
      </Box>
      {onContinueAnyway && (
        <Button size="small" color="error" variant="text" onClick={onContinueAnyway} sx={{ flexShrink: 0, opacity: 0.75 }}>
          Continue anyway
        </Button>
      )}
    </Box>
  );
}

export function ConflictWarningModal({
  open,
  holders,
  onClose,
  onContinueAnyway,
}: {
  open: boolean;
  holders: ConflictHolder[];
  onClose: () => void;
  onContinueAnyway: () => void;
}) {
  const primary = holders[0];
  if (!primary) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Already checked out</DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: 14, color: c.ink3 }}>
          <strong>{primary.displayName}</strong> has had this since {primary.sinceLabel}
          {primary.unpushed ? " with unpushed work" : ""}. Recommend connecting first — nothing
          blocks you if you continue.
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="text" onClick={onContinueAnyway} sx={{ opacity: 0.8 }}>
          Continue anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
